import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonVariant } from '@/shared/ui/Button';
import Checkbox from '@/shared/ui/Checkbox';
import { Select } from '@/shared/components/ui/Select';
import {
  ViewSettings,
  PageSetting,
  PageSize,
  SearchValueType,
  ColumnValueType,
  StopFilterValuesType,
  RefreshPeriodValues,
} from './types';

interface ViewSettingsFormProps {
  initialValues: ViewSettings;
  onSubmit: (values: ViewSettings) => void;
  onCancel: () => void;
  isUpdating: boolean;
}

const ViewSettingsForm: React.FC<ViewSettingsFormProps> = ({ initialValues, onSubmit, onCancel, isUpdating }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<ViewSettings>(initialValues);

  const pageSizeOptions: { label: string; value: PageSize }[] = [
    { label: '10', value: 10 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
    { label: '500', value: 500 },
    { label: '1000', value: 1000 },
  ];
  
  const searchOptions: { label: string; value: SearchValueType }[] = [
    { label: t('viewSettings.automatic'), value: false },
    { label: t('viewSettings.manual'), value: true },
  ];
  
  const columnOptions: { label: string; value: ColumnValueType }[] = [
    { label: t('viewSettings.basic'), value: 'basic' },
    { label: t('viewSettings.advanced'), value: 'advanced' },
    { label: t('viewSettings.full'), value: 'full' },
  ];
  
  const stopFilterOptions: { label: string; value: StopFilterValuesType }[] = [
    { label: t('viewSettings.noFilter'), value: '' },
    { label: t('viewSettings.current'), value: 'recent' },
    { label: t('viewSettings.untilEndOfDay'), value: 'after' },
  ];
  
  const dragAndDropOptions: { label: string; value: string }[] = [
    { label: t('viewSettings.noFilter'), value: '' },
    { label: t('viewSettings.yes'), value: 'true' },
    { label: t('viewSettings.no'), value: 'false' },
  ];
  
  const refreshPeriodOptions: { label: string; value: RefreshPeriodValues }[] = [
    { label: t('viewSettings.none'), value: '' },
    { label: t('viewSettings.refresh1min'), value: 1 },
    { label: t('viewSettings.refresh2min'), value: 2 },
    { label: t('viewSettings.refresh5min'), value: 5 },
    { label: t('viewSettings.refresh10min'), value: 10 },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name.includes('pageSettings')) {
      const [_, indexStr, field] = name.match(/pageSettings\[(\d+)\]\.(.+)/) || [];
      if(!indexStr) return;
      const index = parseInt(indexStr, 10);
      const updatedPageSettings = [...settings.pageSettings];
      
      const key = field as keyof PageSetting;
      
      if(key === 'pageSize') {
        (updatedPageSettings[index] as any)[key] = Number(value) as PageSize;
      } else if (key === 'search') {
        (updatedPageSettings[index] as any)[key] = value === 'true';
      } else {
        (updatedPageSettings[index] as any)[key] = value;
      }
      
      setSettings({ ...settings, pageSettings: updatedPageSettings });

    } else if (type === 'checkbox') {
        setSettings({ ...settings, [name]: checked });
    } else {
      setSettings({ ...settings, [name]: value });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(settings);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="view-settings-form">
      <div className="">
        <div className="flex items-center gap-8 mb-6">
            <h2 className="text-lg font-medium text-text w-[200px]">{t('viewSettings.general')}</h2>
            <Select
                label={t('viewSettings.startingDayOfWeek')}
                name="startingDayOfWeek"
                options={[
                { label: t('viewSettings.sunday'), value: 'Sunday' },
                { label: t('viewSettings.monday'), value: 'Monday' },
                ]}
                value={settings.startingDayOfWeek}
                onChange={handleChange}
                className="w-[300px]"
                data-testid="starting-day-of-week-select"
                
            />
        </div>

        <h2 className="text-lg font-medium text-text mb-4">{t('viewSettings.pages')}</h2>
        <div className="mb-4">
          <Checkbox
            id="floatTableHeaders"
            name="floatTableHeaders"
            label={t('viewSettings.floatTableHeaders')}
            checked={settings.floatTableHeaders}
            onChange={handleChange}
            data-testid="float-table-headers-checkbox"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border border border-border">
            <thead className="bg-surface-alt">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-resetButton ">{t('viewSettings.page')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-resetButton ">{t('viewSettings.pageSize')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-resetButton ">{t('viewSettings.search')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-resetButton ">{t('viewSettings.columns')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-resetButton ">{t('viewSettings.stopFilter')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-resetButton ">{t('viewSettings.dragAndDrop')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-resetButton ">{t('viewSettings.refreshPeriod')}</th>
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-border">
              {settings.pageSettings.map((setting, index) => (
                <tr key={setting.id}>
                  <td className="p-0.5 whitespace-nowrap text-sm font-medium text-text">{t(`viewSettings.${setting.page}`)}</td>
                  <td className="p-0.5 whitespace-nowrap">
                    {setting.pageSize !== undefined && (
                      <Select
                        label=""
                        name={`pageSettings[${index}].pageSize`}
                        options={pageSizeOptions.map(o => ({label: o.label, value: o.value.toString()}))}
                        value={setting.pageSize.toString()}
                        onChange={handleChange}
                        className="w-24"
                        data-testid={`page-size-select-${index}`}
                        direction={index > 5 ? 'up' : 'down'}
                      />
                    )}
                  </td>
                  <td className="p-0.5 whitespace-nowrap">
                    {setting.search !== undefined && (
                      <Select
                        label=""
                        name={`pageSettings[${index}].search`}
                        options={searchOptions.map(o => ({label: o.label, value: o.value.toString()}))}
                        value={String(setting.search)}
                        onChange={handleChange}
                        className="w-32"
                        data-testid={`search-select-${index}`}
                        direction={index > 8 ? 'up' : 'down'}
                      />
                    )}
                  </td>
                  <td className="p-0.5 whitespace-nowrap">
                    {setting.columns !== undefined && (
                      <Select
                        label=""
                        name={`pageSettings[${index}].columns`}
                        options={columnOptions}
                        value={setting.columns}
                        onChange={handleChange}
                        className="w-32"
                        data-testid={`columns-select-${index}`}
                        direction={index > 7 ? 'up' : 'down'}
                      />
                    )}
                  </td>
                  <td className="p-0.5 whitespace-nowrap">
                    {setting.stopFilter !== undefined && (
                      <Select
                        label=""
                        name={`pageSettings[${index}].stopFilter`}
                        options={stopFilterOptions}
                        value={setting.stopFilter}
                        onChange={handleChange}
                        className="w-48"
                        data-testid={`stop-filter-select-${index}`}
                      />
                    )}
                  </td>
                  <td className="p-0.5 whitespace-nowrap">
                    {setting.dragAndDrop !== undefined && (
                      <Select
                        label=""
                        name={`pageSettings[${index}].dragAndDrop`}
                        options={dragAndDropOptions}
                        value={setting.dragAndDrop}
                        onChange={handleChange}
                        className="w-32"
                        data-testid={`drag-drop-select-${index}`}
                      />
                    )}
                  </td>
                  <td className="p-0.5 whitespace-nowrap">
                    {setting.refreshPeriod !== undefined && (
                      <Select
                        label=""
                        name={`pageSettings[${index}].refreshPeriod`}
                        options={refreshPeriodOptions.map(o => ({label: o.label, value: String(o.value)}))}
                        value={String(setting.refreshPeriod)}
                        onChange={handleChange}
                        className="w-32"
                        data-testid={`refresh-select-${index}`}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex justify-end gap-3 p-4 bg-surface-alt border-t border-border">
        <Button type="button" variant={ButtonVariant.Secondary} onClick={onCancel} data-testid="cancel-button">
          {t('viewSettings.cancel')}
        </Button>
        <Button type="submit" variant={ButtonVariant.Primary} data-testid="apply-button" disabled={isUpdating}>
          {isUpdating ? t('viewSettings.applying') : t('viewSettings.apply')}
        </Button>
      </div>
    </form>
  );
};

export default ViewSettingsForm;
